import { z } from "zod";
import { civilDate, monthKey, minor } from "@clareza/domain";
export const MinorSchema = z.string().refine(
  (v) => {
    try {
      minor(v);
      return true;
    } catch {
      return false;
    }
  },
  { message: "Valor monetário inválido.", abort: true },
);
export const DateSchema = z.string().refine((v) => {
  try {
    civilDate(v);
    return true;
  } catch {
    return false;
  }
}, "Data inválida.");
export const MonthSchema = z.string().refine((v) => {
  try {
    monthKey(v);
    return true;
  } catch {
    return false;
  }
}, "Mês inválido.");
export const AccountInput = z
  .object({
    name: z.string().trim().min(1).max(80),
    institution: z.string().trim().min(1).max(80),
    kind: z.enum(["checking", "savings", "credit_card"]),
    currency: z.literal("BRL").default("BRL"),
    balanceMinor: MinorSchema.nullable(),
    balanceAsOf: DateSchema.nullable(),
  })
  .strict()
  .refine(
    (a) => (a.balanceMinor === null) === (a.balanceAsOf === null),
    "Informe valor e data do saldo juntos.",
  );
export const AccountUpdateInput = z
  .object({
    name: AccountInput.shape.name,
    institution: AccountInput.shape.institution,
    balanceMinor: MinorSchema.nullable(),
    balanceAsOf: DateSchema.nullable(),
    version: z.number().int().positive().max(2147483647),
  })
  .strict()
  .refine(
    (a) => (a.balanceMinor === null) === (a.balanceAsOf === null),
    "Informe valor e data do saldo juntos.",
  );
export const MovementUpdateInput = z
  .object({
    version: z.number().int().positive().max(2147483647),
    description: z.string().trim().min(1).max(180),
    amountMinor: MinorSchema.refine((v) => minor(v) !== 0n),
    occurredOn: DateSchema,
    competenceMonth: MonthSchema,
  })
  .strict();
export const MovementVoidInput = z
  .object({
    version: z.number().int().positive().max(2147483647),
    confirmed: z.literal(true),
  })
  .strict();
export const MovementInput = z
  .object({
    accountId: z.uuid(),
    description: z.string().trim().min(1).max(180),
    amountMinor: MinorSchema,
    currency: z.literal("BRL").default("BRL"),
    kind: z.enum([
      "income",
      "expense",
      "card_purchase",
      "card_payment",
      "own_transfer",
      "refund",
    ]),
    occurredOn: DateSchema,
    competenceMonth: MonthSchema,
    category: z.string().trim().min(1).max(60),
    billId: z.uuid().nullable().default(null),
  })
  .strict()
  .refine(
    (t) =>
      minor(t.amountMinor) !== 0n &&
      (t.kind === "own_transfer" ||
        (["income", "refund"].includes(t.kind)
          ? minor(t.amountMinor) > 0n
          : minor(t.amountMinor) < 0n)),
    "Confira o sinal do valor.",
  )
  .refine(
    (t) =>
      (t.kind !== "card_purchase" && t.kind !== "card_payment") ||
      t.billId !== null,
    "Vincule o lançamento ao ciclo da fatura.",
  );
export const BillInput = z
  .object({
    accountId: z.uuid(),
    competenceMonth: MonthSchema,
    closingOn: DateSchema.nullable(),
    dueOn: DateSchema.nullable(),
    totalMinor: MinorSchema.nullable(),
  })
  .strict()
  .refine(
    (b) => b.totalMinor === null || minor(b.totalMinor) >= 0n,
    "O total deve ser positivo ou zero.",
  )
  .refine(
    (b) => b.closingOn === null || b.dueOn === null || b.closingOn <= b.dueOn,
    "O fechamento deve ocorrer até o vencimento.",
  );
export const BillUpdateInput = z
  .object({
    closingOn: DateSchema.nullable(),
    dueOn: DateSchema.nullable(),
    totalMinor: MinorSchema.nullable(),
    version: z.number().int().positive(),
  })
  .strict()
  .refine(
    (b) => b.totalMinor === null || minor(b.totalMinor) >= 0n,
    "O total deve ser positivo ou zero.",
  )
  .refine(
    (b) => b.closingOn === null || b.dueOn === null || b.closingOn <= b.dueOn,
    "O fechamento deve ocorrer até o vencimento.",
  );
export const OccurrenceInput = z
  .object({
    description: z.string().trim().min(1).max(180),
    amountMinor: MinorSchema.nullable(),
    currency: z.literal("BRL").default("BRL"),
    kind: z.enum(["income", "expense"]),
    competenceMonth: MonthSchema,
    dueOn: DateSchema.nullable(),
  })
  .strict()
  .refine(
    (p) => p.amountMinor === null || minor(p.amountMinor) >= 0n,
    "Use um valor positivo.",
  );
export const OccurrenceVersionInput = z
  .object({
    version: z.number().int().positive().max(2147483647),
  })
  .strict();
const RecurrenceValues = z.object({
  description: z.string().trim().min(1).max(180),
  amountMinor: MinorSchema.nullable().refine(
    (v) => v === null || minor(v) >= 0n,
  ),
  anchorDay: z.number().int().min(1).max(31),
  endMonth: MonthSchema.nullable(),
});
export const RecurrenceInput = RecurrenceValues.extend({
  kind: z.enum(["income", "expense"]),
  currency: z.literal("BRL").default("BRL"),
  startMonth: MonthSchema,
  count: z.number().int().min(1).max(12).default(3),
})
  .strict()
  .refine((v) => v.endMonth === null || v.endMonth >= v.startMonth);
export const RecurrenceUpdateInput = RecurrenceValues.extend({
  version: z.number().int().positive().max(2147483647),
  effectiveMonth: MonthSchema,
})
  .strict()
  .refine((v) => v.endMonth === null || v.endMonth >= v.effectiveMonth);
export const RecurrenceStopInput = OccurrenceVersionInput.extend({
  effectiveMonth: MonthSchema,
}).strict();
export const RecurrenceGenerateInput = OccurrenceVersionInput.extend({
  fromMonth: MonthSchema,
  count: z.number().int().min(1).max(12).default(3),
}).strict();
export const OccurrenceUpdateInput = OccurrenceInput.safeExtend({
  version: z.number().int().positive().max(2147483647),
});
export const OccurrenceMatchInput = OccurrenceVersionInput.extend({
  transactionId: z.uuid(),
  transactionVersion: z.number().int().positive().max(2147483647),
  confirmed: z.literal(true),
  acceptAmountDifference: z.boolean().default(false),
}).strict();
export const OverrideInput = z
  .object({
    category: z.string().trim().min(1).max(60),
    version: z.number().int().positive(),
  })
  .strict();
export const ScenarioInput = z
  .object({
    baseSurplusMinor: MinorSchema.nullable(),
    costs: z.record(z.string().max(60), MinorSchema.nullable()),
  })
  .strict()
  .refine(
    (s) => Object.values(s.costs).every((v) => v === null || minor(v) >= 0n),
    "Custos devem ser positivos.",
  );
const NonNegativeMinor = MinorSchema.refine((v) => minor(v) >= 0n);
export const ReserveSettingsInput = z
  .object({
    version: z.number().int().min(0).max(2147483647),
    accountId: z.uuid().nullable(),
    confirmedMinor: NonNegativeMinor.nullable(),
    confirmedOn: DateSchema.nullable(),
    targetMinor: NonNegativeMinor.nullable(),
    targetMonths: z.number().int().min(1).max(60).nullable(),
    monthlyExpenseMinor: NonNegativeMinor.nullable(),
    confirmed: z.literal(true),
  })
  .strict()
  .refine((v) => (v.confirmedMinor === null) === (v.confirmedOn === null))
  .refine((v) => v.confirmedMinor === null || v.accountId !== null)
  .refine((v) => v.targetMinor === null || v.targetMonths === null)
  .refine((v) => v.monthlyExpenseMinor === null || v.targetMonths !== null);
export const ReservePlanInput = z
  .object({
    version: z.number().int().min(0).max(2147483647),
    month: MonthSchema,
    plannedMinor: NonNegativeMinor.nullable(),
  })
  .strict();
export const OwnTransferInput = z
  .object({
    fromAccountId: z.uuid(),
    toAccountId: z.uuid(),
    description: z.string().trim().min(1).max(180),
    amountMinor: MinorSchema.refine((v) => minor(v) > 0n),
    feeMinor: NonNegativeMinor.nullable(),
    occurredOn: DateSchema,
  })
  .strict()
  .refine((v) => v.fromAccountId !== v.toAccountId);
export const ReserveContributionInput = z
  .object({
    transferId: z.uuid(),
    transferVersion: z.number().int().positive(),
    reserveVersion: z.number().int().positive(),
    amountMinor: MinorSchema.refine((v) => minor(v) > 0n),
    confirmed: z.literal(true),
  })
  .strict();
export const RecordVoidInput = z
  .object({ version: z.number().int().positive(), confirmed: z.literal(true) })
  .strict();
export const InstallmentGroupInput = z
  .object({
    accountId: z.uuid(),
    description: z.string().trim().min(1).max(180),
    totalAmountMinor: NonNegativeMinor.nullable(),
    totalCount: z.number().int().min(1).max(600).nullable(),
    startMonth: MonthSchema,
  })
  .strict()
  .refine(
    (g) =>
      g.totalAmountMinor === null ||
      g.totalCount === null ||
      minor(g.totalAmountMinor) >= BigInt(g.totalCount),
    "O valor total em centavos precisa ser pelo menos igual à quantidade de parcelas.",
  );
export const InstallmentItemInput = z
  .object({
    groupId: z.uuid(),
    installmentNumber: z.number().int().min(1).max(600).nullable(),
    competenceMonth: MonthSchema,
    amountMinor: NonNegativeMinor.refine((v) => minor(v) > 0n),
    transactionId: z.uuid().nullable().optional(),
  })
  .strict();
export const InstallmentCancelInput = RecordVoidInput;
export const CsvMappingSchema = z
  .object({
    dateColumn: z.string().trim().min(1).max(100),
    descriptionColumn: z.string().trim().min(1).max(100),
    amountColumn: z.string().trim().min(1).max(100),
    categoryColumn: z.string().trim().max(100).optional(),
    typeColumn: z.string().trim().max(100).optional(),
    delimiter: z.enum([",", ";", "\t"]).optional(),
  })
  .strict();
export const CsvPreviewInput = z
  .object({
    accountId: z.uuid(),
    csvContent: z.string().min(1).max(5_000_000),
    mapping: CsvMappingSchema,
  })
  .strict();
export const CsvImportItemInput = z
  .object({
    occurredOn: DateSchema,
    description: z.string().trim().min(1).max(180),
    amountMinor: MinorSchema.refine((v) => minor(v) > 0n),
    kind: z.enum(["expense", "income"]),
    category: z.string().trim().min(1).max(60),
  })
  .strict();
export const CsvCommitInput = z
  .object({
    accountId: z.uuid(),
    fileName: z.string().trim().min(1).max(255),
    fileSha256: z.string().trim().length(64),
    items: z.array(CsvImportItemInput).min(1).max(5000),
  })
  .strict();
export const CsvRevertBatchInput = RecordVoidInput;
export const OpenFinanceRegisterItemInput = z
  .object({
    itemId: z.string().trim().min(1).max(120),
  })
  .strict();
export const OpenFinanceSyncItemInput = z
  .object({
    itemId: z.string().trim().min(1).max(120),
  })
  .strict();
export const categories = [
  "Alimentação",
  "Moradia",
  "Transporte",
  "Saúde",
  "Lazer",
  "Educação",
  "Compras",
  "Serviços",
  "Renda",
  "Transferência",
  "A revisar",
] as const;
